import {
  fetch_all_onboarding_cases,
  fetch_onboarding_entity_type_questions,
  fetch_onboarding_entity_types,
  fetch_onboarding_forms_for_entity_type
} from '@/services/onboarding_journey_service';
import { resolve_onboarding_journey_filter_key } from '@/services/onboarding_journey_filter_service';
import { resolve_template_sla_label } from '@/views/onboarding_system_views/onboarding_ui_helpers';

const JOURNEY_COLORS = new Map([
  ['Supplier', '#416f6e'],
  ['Employee', '#417d71'],
  ['Merchant', '#be8721'],
  ['Partner', '#ba426a'],
  ['Intern', '#6642cc'],
  ['Client', '#355f73']
]);

function normalize_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function matches_active_journey(entity_type = {}, journey_filter = '') {
  const normalized_filter = normalize_text(journey_filter);
  if (!normalized_filter) return true;

  const candidate_values = [
    entity_type?.id,
    entity_type?.entity_type_id,
    entity_type?.journey_key,
    entity_type?.name
  ];
  return candidate_values.some((value) => normalize_text(value) === normalized_filter);
}

const question_icon = (question) => {
  const type = String(question?.type || '').toLowerCase();

  if (type.includes('file') || type.includes('document') || type.includes('upload')) {
    return 'bi-file-earmark-text';
  }

  if (type.includes('date')) {
    return 'bi-calendar-event';
  }

  if (type.includes('select') || type.includes('choice')) {
    return 'bi-list-check';
  }

  return 'bi-input-cursor-text';
};

const template_description = (questions) => {
  const labels = questions.slice(0, 4).map((question) => question.label).filter(Boolean);
  return labels.length ? labels.join(', ') : 'Configured onboarding template.';
};

const build_playbook_groups = (questions) => {
  const grouped = new Map();

  questions.forEach((question) => {
    const group_name = question.section_name || 'Required information';
    if (!grouped.has(group_name)) {
      grouped.set(group_name, []);
    }

    grouped.get(group_name).push({
      title: question.label,
      type: question.type || 'Field',
      icon: question_icon(question),
      required: question.is_mandatory !== false
    });
  });

  return Array.from(grouped.entries()).map(([title, items], index) => ({
    number: String(index + 1),
    title,
    items
  }));
};

export default {
  name: 'OnboardingManageTemplates',
  data: () => ({
    is_loading_templates: false,
    templates_error: '',
    selected_template: null,
    is_loading_template_detail: false,
    is_detail_fullscreen: false,
    playbook_groups: [],
    templates: []
  }),
  computed: {
    active_journey_filter() {
      return resolve_onboarding_journey_filter_key(this.$route);
    }
  },
  mounted() {
    this.load_templates();
  },
  watch: {
    active_journey_filter() {
      this.close_template_detail();
      this.load_templates();
    }
  },
  methods: {
    async load_templates() {
      this.is_loading_templates = true;
      this.templates_error = '';

      try {
        const [entity_types, cases] = await Promise.all([
          fetch_onboarding_entity_types(),
          fetch_all_onboarding_cases().catch(() => [])
        ]);
        const configured_journeys = entity_types.filter((entity_type) => (
          entity_type?.is_active !== false
          && !entity_type.is_unconfigured
          && matches_active_journey(entity_type, this.active_journey_filter)
        ));
        const template_groups = await Promise.all(
          configured_journeys.map(async (entity_type) => {
            const forms = await fetch_onboarding_forms_for_entity_type(entity_type.id);

            return forms.map((form) => {
              const journey = entity_type.name;
              const in_use_count = cases.filter((case_item) => (
                String(case_item.entity_type_id || '') === String(entity_type.id)
              )).length;

              return {
                id: `${entity_type.id}-${form.id}`,
                form_id: form.id,
                entity_type_id: entity_type.id,
                name: form.name,
                type: journey,
                meta: `${in_use_count} onboardings`,
                description: form.description || form.form_description || 'Configured onboarding template.',
                steps: 'View playbook',
                status: 'Synced',
                initial: journey.charAt(0).toUpperCase(),
                color: JOURNEY_COLORS.get(journey) || '#355f73',
                journey,
                applies_to: form.description || form.form_description || `${journey} onboarding`,
                owner: form.owner || form.created_by_name || 'Configured in backend',
                target_sla: resolve_template_sla_label(form),
                in_use: `${in_use_count} onboardings`,
                stages: '-',
                steps_count: '-',
                required: '-',
                playbook_groups: null
              };
            });
          })
        );

        this.templates = template_groups.flat();
      } catch (error) {
        this.templates = [];
        this.templates_error = error?.response?.data?.message || error?.message || 'Unable to load templates.';
      } finally {
        this.is_loading_templates = false;
      }
    },
    async open_template_detail(template) {
      this.selected_template = template;
      this.playbook_groups = [];
      this.is_loading_template_detail = true;
      this.is_detail_fullscreen = false;

      try {
        const questions = await fetch_onboarding_entity_type_questions(template.entity_type_id).catch(() => []);
        const form_questions = questions.filter((question) => String(question.form_id || '') === String(template.form_id));
        const playbook_groups = build_playbook_groups(form_questions);
        const selected_template = {
          ...template,
          description: template_description(form_questions),
          steps: `${form_questions.length} questions`,
          steps_count: String(form_questions.length),
          required: String(form_questions.filter((question) => question.is_mandatory !== false).length),
          playbook_groups
        };
        this.selected_template = selected_template;
        this.playbook_groups = playbook_groups;
      } finally {
        this.is_loading_template_detail = false;
      }
    },
    close_template_detail() {
      this.selected_template = null;
      this.is_loading_template_detail = false;
      this.playbook_groups = [];
      this.is_detail_fullscreen = false;
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    }
  }
};
